import {view, TextArea, TextField} from '@sail/ui';
import * as React from 'react';
import {useIsFetching, useIsMutating} from 'react-query';
import {message, useIntl} from '@sail/intl';
import {useAnalytics} from '@sail/observability';
import type {UploadedFile} from '../inputs';
import {
  FileUploaderFileList,
  FileUploaderInput,
  RETRIEVE_FILE_KEY,
  UPLOAD_FILE_KEY,
} from '../inputs';
import {EmbeddedSupportError} from './EmbeddedSupportError';
import {EmbeddedSupportSuccess} from './EmbeddedSupportSuccess';
import {AnalyticsKeys} from '../data-layer-client/analyticsKeys';
import {useMerchantData} from '../context/merchant/MerchantDataContext';
import {Button} from '../SharedComponents/Button/Button';
import {Overlay} from '../SharedComponents/Overlay/Overlay';
import useCreateSupportEmailMutation from './queries/useCreateSupportEmailMutation';

const messages = {
  contactSupport: message({
    id: 'connectEmbedLib.embeddedSupport.contactSupport',
    defaultMessage: 'Contact Support',
    description: 'Title for the contact support module',
  }),

  contactSupportSubtitle: message({
    id: 'connectEmbedLib.embeddedSupport.contactSupportSubtitle',

    defaultMessage:
      'You should expect a response from one of our support agents in the next 4 - 7 days.',

    description: 'Subtitle for the contact support module',
  }),

  submit: message({
    id: 'connectEmbedLib.embeddedSupport.submit',
    defaultMessage: 'Submit',
    description: 'String displayed as a label in the body component',
  }),

  whatIsYourQuestion: message({
    id: 'connectEmbedLib.embeddedSupport.whatIsYourQuestion',
    defaultMessage: "What's your question?",

    description:
      'String displayed as a label prompting the user to enter a subject on the contact support form.',
  }),

  tellUsMore: message({
    id: 'connectEmbedLib.embeddedSupport.tellUsMore',
    defaultMessage: 'Tell us more - How can we help?',

    description:
      'Label displayed as part of the contact support form, prompting the user to input a text for their contact support request.',
  }),

  email: message({
    id: 'connectEmbedLib.embeddedSupport.email',
    defaultMessage: 'Your email',

    description:
      'Label for a field where a user should input the email address we will contact as part of the support flow.',
  }),

  required: message({
    id: 'connectEmbedLib.embeddedSupport.required',
    defaultMessage: 'This field is required.',
    description: 'Error message if a user does not fill out a required field',
  }),
};

const MAXIMUM_FILE_SIZE = 4e6; // 4MB

export const EmbeddedSupport = () => {
  const intl = useIntl();
  const merchantData = useMerchantData();
  const [body, setBody] = React.useState<string>('');
  const [subject, setSubject] = React.useState<string>(
    'Contact support about your problem.',
  );
  const [email, setEmail] = React.useState<string>('');
  // Uploaded files
  const [files, setFiles] = React.useState<UploadedFile[]>([]);
  const [isValidated, setIsValidated] = React.useState<Record<string, boolean>>(
    {
      body: true,
      subject: true,
      email: true,
    },
  );
  const analytics = useAnalytics();
  const isUploadingFile = useIsMutating([UPLOAD_FILE_KEY]);
  const isRetrievingFile = useIsFetching([RETRIEVE_FILE_KEY]);
  const isUploadingOrRetrievingFiles = isUploadingFile + isRetrievingFile > 0;
  const allFieldsFilled = body && subject && email;

  const onSubmit = async () => {
    if (allFieldsFilled) {
      analytics.action(AnalyticsKeys.Action.SUPPORT_CREATION);
      const input = {
        body,
        cc_email: email,
        embedded_component: null,
        subject: `${!merchantData?.livemode ? 'Test123_' : ''}${subject}`,
        file_token: files && files?.length > 0 ? files[0].id : null,
      } as const;
      await createSupportEmail({
        variables: {
          input,
        },
      });
    }
  };
  const validateFields = () => {
    setIsValidated({
      body: !!body,
      subject: !!subject,
      email: !!email,
    });
  };

  const [createSupportEmail, {data, error, loading, called}] =
    useCreateSupportEmailMutation();

  if (error) {
    return <EmbeddedSupportError />;
  } else if (data) {
    return <EmbeddedSupportSuccess />;
  }
  return (
    <>
      {(!called || loading) && (
        <view.div
          css={{
            stack: 'y',
            gap: 'medium',
          }}
        >
          <Overlay.Title>
            {intl.formatMessage(messages.contactSupport)}
          </Overlay.Title>
          <Overlay.Subtitle>
            {intl.formatMessage(messages.contactSupportSubtitle)}
          </Overlay.Subtitle>
          <TextField
            data-testid="contact-support-form-subject"
            label={intl.formatMessage(messages.whatIsYourQuestion)}
            maxLength={500}
            onChange={(e) => setSubject(e.target.value)}
            value={subject}
            readOnly={loading}
            error={
              isValidated.subject ? '' : intl.formatMessage(messages.required)
            }
          />
          <TextArea
            data-testid="contact-support-form-body"
            label={intl.formatMessage(messages.tellUsMore)}
            maxLength={500}
            onChange={(e) => setBody(e.target.value)}
            value={body}
            readOnly={loading}
            error={
              isValidated.body ? '' : intl.formatMessage(messages.required)
            }
          />
          <TextField
            data-testid="contact-support-form-email"
            label={intl.formatMessage(messages.email)}
            type="email"
            maxLength={500}
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            readOnly={loading}
            error={
              isValidated.email ? '' : intl.formatMessage(messages.required)
            }
          />
          <FileUploaderInput
            onChange={(f) => {
              setFiles(f);
            }}
            uploadedFiles={files}
            singleFile
          />
          <FileUploaderFileList
            disabled={loading}
            maxSize={MAXIMUM_FILE_SIZE}
            onChange={(f) => {
              setFiles(f);
            }}
            onRemove={() => {
              setFiles([]);
            }}
            purpose="embedded_support_upload"
            showFileSize
            showPreview
            showSizeUploaded
            uploadedFiles={files}
          />
          <Button
            type="primary"
            css={{
              marginTop: 'large',
            }}
            data-testid="submit-contact-support-form-button"
            disabled={loading || isUploadingOrRetrievingFiles}
            onPress={() => {
              validateFields();
              onSubmit();
            }}
            pending={loading}
            maximized
          >
            {intl.formatMessage(messages.submit)}
          </Button>
        </view.div>
      )}
    </>
  );
};
